// Page Actu Mobile - Feed communautaire avec filtres
// Header simple Abracadabati + Filtres Tous/Mes amis/Mes Pros

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, MessageCircle, Share2, Bookmark, MoreHorizontal,
  ArrowLeft, Users, User, Briefcase, Filter, Loader2,
  MapPin, Image as ImageIcon, Play, Eye, RefreshCw, Search, X,
  Clock, TrendingUp, ArrowRight, Tag, SlidersHorizontal,
  Package, Wrench, ExternalLink, BadgeCheck, ChevronRight,
  Camera, Video, Smile, Link2, Globe, Lock, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import StoriesBar from '../../components/stories/StoriesBar';
import { useImmersiveScrollContext } from '../../context/ImmersiveScrollContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Liste des métiers pour le filtre - Style Premium SANS EMOJIS
const METIERS = [
  { value: 'tous', label: 'Tous', color: '#1E3A5F' },
  { value: 'pisciniste', label: 'Pisciniste', color: '#06B6D4' },
  { value: 'plombier', label: 'Plombier', color: '#3B82F6' },
  { value: 'electricien', label: 'Électricien', color: '#EAB308' },
  { value: 'macon', label: 'Maçon', color: '#EA580C' },
  { value: 'carreleur', label: 'Carreleur', color: '#10B981' },
  { value: 'peintre', label: 'Peintre', color: '#EC4899' },
  { value: 'menuisier', label: 'Menuisier', color: '#D97706' },
  { value: 'couvreur', label: 'Couvreur', color: '#8B5CF6' },
  { value: 'chauffagiste', label: 'Chauffagiste', color: '#EF4444' },
  { value: 'paysagiste', label: 'Paysagiste', color: '#22C55E' },
  { value: 'climaticien', label: 'Climaticien', color: '#0891B2' },
  { value: 'jardinier', label: 'Jardinier', color: '#16A34A' },
  { value: 'facadier', label: 'Façadier', color: '#A855F7' },
  { value: 'serrurier', label: 'Serrurier', color: '#64748B' },
  { value: 'charpentier', label: 'Charpentier', color: '#92400E' },
  { value: 'plaquiste', label: 'Plaquiste', color: '#6366F1' },
  { value: 'cuisiniste', label: 'Cuisiniste', color: '#F97316' },
  { value: 'architecte', label: 'Architecte', color: '#1E3A5F' },
];

// Couleurs par métier
const METIER_COLORS = {
  'Plombier': '#3B82F6',
  'Électricien': '#EAB308',
  'Maçon': '#EA580C',
  'Carreleur': '#10B981',
  'Menuisier': '#D97706',
  'Peintre': '#EC4899',
  'Couvreur': '#8B5CF6',
  'Chauffagiste': '#EF4444',
  'Pisciniste': '#06B6D4',
  'Paysagiste': '#22C55E',
};

// Composant Produits/Prestations liés au post
function LinkedProductsSection({ products, services, linkedProduct, linkedService }) {
  // Support des deux formats: array ou single object
  // Vérifier si array non vide OU single object
  const productsList = (products && products.length > 0) ? products : (linkedProduct ? [linkedProduct] : []);
  const servicesList = (services && services.length > 0) ? services : (linkedService ? [linkedService] : []);
  
  const hasProducts = productsList.length > 0;
  const hasServices = servicesList.length > 0;
  
  if (!hasProducts && !hasServices) return null;
  
  return (
    <div className="px-3 pb-2 border-t border-gray-100 mt-2 pt-2">
      {/* Produits liés */}
      {hasProducts && (
        <div className="mb-2">
          <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
            <Package size={10} />
            Produit lié
          </h4>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {productsList.map((product, idx) => (
              <Link
                key={product.id || product.product_id || idx}
                to={`/produit/${product.product_id || product.id}`}
                className="flex-shrink-0 flex items-center gap-2 bg-gray-50 hover:bg-orange-50 rounded-lg p-2 pr-3 transition-colors group border border-gray-100"
              >
                <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center overflow-hidden">
                  {product.image_url || product.image ? (
                    <img 
                      src={product.image_url || product.image} 
                      alt={product.name || product.title} 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <Package size={14} className="text-gray-400" />
                  )}
                </div>
                <div className="max-w-[120px]">
                  <p className="text-xs font-medium text-gray-900 group-hover:text-orange-600 line-clamp-1">
                    {product.name || product.title}
                  </p>
                  {(product.price || product.promo_price) && (
                    <p className="text-[10px] text-orange-600 font-semibold">
                      {product.promo_price || product.price} €
                    </p>
                  )}
                </div>
                <ExternalLink size={10} className="text-gray-400 group-hover:text-orange-500" />
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {/* Prestations liées */}
      {hasServices && (
        <div>
          <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
            <Wrench size={10} />
            Prestation liée
          </h4>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {servicesList.map((service, idx) => (
              <Link
                key={service.id || service.prestation_id || idx}
                to={`/prestation/${service.prestation_id || service.id}`}
                className="flex-shrink-0 flex items-center gap-2 bg-gray-50 hover:bg-orange-50 rounded-lg p-2 pr-3 transition-colors group border border-gray-100"
              >
                <div className="w-9 h-9 bg-[#1E3A5F] rounded-lg flex items-center justify-center">
                  <Wrench size={14} className="text-white" />
                </div>
                <div className="max-w-[120px]">
                  <p className="text-xs font-medium text-gray-900 group-hover:text-orange-600 line-clamp-1">
                    {service.name || service.title}
                  </p>
                  {service.price_from && (
                    <p className="text-[10px] text-gray-500">
                      À partir de {service.price_from} €
                    </p>
                  )}
                </div>
                <ExternalLink size={10} className="text-gray-400 group-hover:text-orange-500" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Composant Post Card
function PostCard({ post }) {
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes || 0);
  const [saved, setSaved] = useState(false);
  
  const author = post.author || {};
  const isPro = author.is_pro || author.type === 'pro' || post.is_pro;
  const avatarColor = METIER_COLORS[author.metier] || author.color || '#1E3A5F';
  
  const handleLike = (e) => {
    e.stopPropagation();
    setLiked(!liked);
    setLikesCount(prev => liked ? prev - 1 : prev + 1);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Récemment';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      
      if (diffHours < 1) return 'À l\'instant';
      if (diffHours < 24) return `Il y a ${diffHours}h`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `Il y a ${diffDays}j`;
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    } catch {
      return 'Récemment';
    }
  };
  
  const hasImages = post.images?.length > 0 || post.thumbnail;
  const mainImage = post.images?.[0] || post.thumbnail;
  
  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border-b border-gray-100"
    >
      {/* Header du post */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          {author.avatar ? (
            <img 
              src={author.avatar} 
              alt={author.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
              style={{ background: avatarColor }}
            >
              {(author.name || 'A')[0].toUpperCase()}
            </div>
          )}
          
          {/* Infos auteur */}
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-sm text-gray-900">{author.name || 'Utilisateur'}</span>
              {isPro && (
                <span className="px-1.5 py-0.5 bg-[#1E3A5F] text-white rounded text-[9px] font-bold">PRO</span>
              )}
              {author.verified && (
                <BadgeCheck size={14} className="text-blue-500" />
              )}
            </div>
            {/* Métier + Ville */}
            <div className="flex items-center gap-1 text-xs text-gray-500">
              {author.metier && (
                <span className="text-[#1E3A5F] font-medium">{author.metier}</span>
              )}
              {author.metier && author.city && <span>•</span>}
              {author.city && (
                <span className="flex items-center gap-0.5">
                  <MapPin size={10} />
                  {author.city}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <button className="p-1.5 hover:bg-gray-100 rounded-full">
          <MoreHorizontal size={18} className="text-gray-500" />
        </button>
      </div>
      
      {/* Image du post */}
      {hasImages && mainImage && (
        <div className="relative aspect-square bg-gray-100">
          <img 
            src={mainImage}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {post.images?.length > 1 && (
            <div className="absolute top-2 right-2 px-2 py-1 bg-black/60 rounded-full text-white text-xs">
              1/{post.images.length}
            </div>
          )}
        </div>
      )}
      
      {/* Actions */}
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4">
            <button onClick={handleLike} className="flex items-center gap-1">
              <Heart 
                size={22} 
                className={liked ? 'fill-red-500 text-red-500' : 'text-gray-700'}
              />
            </button>
            <button className="flex items-center gap-1">
              <MessageCircle size={22} className="text-gray-700" />
            </button>
            <button>
              <Share2 size={20} className="text-gray-700" />
            </button>
          </div>
          <button onClick={() => setSaved(!saved)}>
            <Bookmark 
              size={22} 
              className={saved ? 'fill-gray-900 text-gray-900' : 'text-gray-700'}
            />
          </button>
        </div>
        
        {/* Likes */}
        <p className="font-semibold text-sm text-gray-900 mb-1">
          {likesCount} J'aime
        </p>
        
        {/* Contenu */}
        {post.content && (
          <p className="text-sm text-gray-800 mb-1">
            <span className="font-semibold">{author.name}</span>{' '}
            {post.content.length > 100 ? `${post.content.slice(0, 100)}...` : post.content}
          </p>
        )}
        
        {/* Comments preview */}
        {post.comments > 0 && (
          <button className="text-sm text-gray-500 mb-1">
            Voir les {post.comments} commentaires
          </button>
        )}
        
        {/* Date */}
        <p className="text-[10px] text-gray-400 uppercase">
          {formatDate(post.created_at)}
        </p>
      </div>
      
      {/* Produits/Prestations liés */}
      <LinkedProductsSection 
        products={post.linked_products}
        services={post.linked_services}
        linkedProduct={post.linkedProduct}
        linkedService={post.linkedService}
      />
    </motion.article>
  );
}

export default function MobileActuPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filtres
  const [filterTab, setFilterTab] = useState('tout'); // tout, a_cote, suivis, promos
  const [filterMetier, setFilterMetier] = useState('tous');
  const [showMetierFilter, setShowMetierFilter] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  
  // Popup inscription pour les stories
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  
  // Scroll behavior depuis le context global (synchronise header et footer)
  const { isHidden } = useImmersiveScrollContext();
  
  // Vérifier si l'utilisateur est connecté
  const isLoggedIn = () => {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('client_token');
    const userId = localStorage.getItem('user_id');
    return !!(token || userId);
  };
  
  // Handler pour créer une story
  const handleCreateStory = () => {
    if (isLoggedIn()) {
      navigate('/cockpit-pro/reseau-social/story/create');
    } else {
      setShowAuthPopup(true);
    }
  };
  
  // Suggestions populaires pour le feed Actualité
  const POPULAR_SUGGESTIONS = [
    { query: 'Plombier', icon: '💧', type: 'métier' },
    { query: 'Électricien', icon: '⚡', type: 'métier' },
    { query: 'Rénovation', icon: '🏠', type: 'sujet' },
    { query: 'Chantier', icon: '👷', type: 'sujet' },
    { query: 'Avant/Après', icon: '✨', type: 'sujet' },
    { query: 'Conseil', icon: '💡', type: 'sujet' },
  ];
  
  // Charger les recherches récentes
  useEffect(() => {
    const saved = localStorage.getItem('actu_recent_searches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved).slice(0, 5));
      } catch {}
    }
  }, []);
  
  // Sauvegarder une recherche
  const saveRecentSearch = (query) => {
    if (!query.trim()) return;
    const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('actu_recent_searches', JSON.stringify(updated));
  };
  
  // Exécuter la recherche
  const handleSearchSubmit = (query) => {
    if (query.trim()) {
      saveRecentSearch(query);
      setSearchQuery(query);
    }
    setShowSuggestions(false);
  };
  
  // Supprimer une recherche récente
  const removeRecentSearch = (searchToRemove, e) => {
    e.stopPropagation();
    const updated = recentSearches.filter(s => s !== searchToRemove);
    setRecentSearches(updated);
    localStorage.setItem('actu_recent_searches', JSON.stringify(updated));
  };
  
  // Filtrer les suggestions selon la saisie
  const filteredSuggestions = searchQuery.trim()
    ? POPULAR_SUGGESTIONS.filter(s => 
        s.query.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : POPULAR_SUGGESTIONS;
  
  // Géolocalisation
  const [userLocation, setUserLocation] = useState(null);
  
  // Charger les posts depuis le nouvel endpoint /api/feed
  const fetchPosts = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      // Construire l'URL avec les paramètres
      const params = new URLSearchParams();
      params.append('tab', filterTab);
      params.append('limit', '30');
      
      // Filtre par métier
      if (filterMetier !== 'tous') {
        params.append('metier', filterMetier);
      }
      
      // Géolocalisation pour "À côté"
      if (filterTab === 'a_cote' && userLocation) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
        params.append('radius', '30');
      }
      
      // User ID pour "Suivis" (récupérer depuis localStorage ou context)
      const userId = localStorage.getItem('user_id') || 'demo_user';
      if (filterTab === 'suivis') {
        params.append('user_id', userId);
      }
      
      const url = `${API_URL}/api/feed?${params.toString()}`;
      const res = await fetch(url);
      
      if (res.ok) {
        const data = await res.json();
        setPosts(data.items || []);
      }
    } catch (err) {
      console.error('Erreur chargement feed:', err);
    }
    
    setLoading(false);
    setRefreshing(false);
  }, [filterTab, filterMetier, userLocation]);
  
  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);
  
  // Gérer le changement d'onglet "À côté" - demander la géoloc
  useEffect(() => {
    if (filterTab === 'a_cote' && !userLocation) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
            toast.success('Position détectée - rayon 30km');
          },
          (error) => {
            toast.error('Localisation désactivée - active-la pour ce filtre');
          }
        );
      }
    }
  }, [filterTab, userLocation]);
  
  // Les posts sont déjà filtrés côté backend, on garde juste le filtre recherche
  const filteredPosts = posts.filter(p => {
    const author = p.author || {};
    
    // Filtre par recherche locale
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchContent = p.content?.toLowerCase().includes(query);
      const matchTitle = p.title?.toLowerCase().includes(query);
      const matchAuthor = author.name?.toLowerCase().includes(query);
      const matchMetier = author.metier?.toLowerCase().includes(query);
      const matchCity = author.city?.toLowerCase().includes(query);
      return matchContent || matchTitle || matchAuthor || matchMetier || matchCity;
    }
    
    return true;
  });
  
  return (
    <div className="min-h-screen bg-gray-50" style={{ paddingTop: '190px', paddingBottom: '80px' }}>
      {/* Header fixe qui se cache/affiche au scroll - Positionné sous le TopBanner global (94px) */}
      <div 
        className={`fixed left-0 right-0 z-[55] transition-transform duration-300 ${
          isHidden ? '-translate-y-full' : 'translate-y-0'
        }`}
        style={{ top: '94px' }}
      >
        {/* Bandeau bêta supprimé - géré par TopBanner.jsx global */}
        
        {/* Header Actualité */}
        <header className="bg-white border-b border-gray-200">
        <div className="flex items-center justify-between h-12 px-3">
          {/* Titre ou Barre de recherche intelligente */}
          <AnimatePresence mode="wait">
            {showSearch ? (
              <motion.div
                key="search"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex-1 flex items-center gap-2 mr-2 relative"
              >
                <div className="flex-1 relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                  <input
                    type="text"
                    placeholder="Rechercher un pro, un sujet..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearchSubmit(searchQuery);
                      }
                    }}
                    autoFocus
                    className="w-full pl-9 pr-3 py-2 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white"
                  />
                </div>
                <button 
                  onClick={() => { 
                    setShowSearch(false); 
                    setSearchQuery(''); 
                    setShowSuggestions(false);
                  }}
                  className="p-1.5 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} className="text-gray-500" />
                </button>
                
                {/* Dropdown Suggestions Intelligentes */}
                <AnimatePresence>
                  {showSuggestions && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50"
                      style={{ maxHeight: '60vh', overflowY: 'auto' }}
                    >
                      {/* Recherches récentes */}
                      {recentSearches.length > 0 && !searchQuery.trim() && (
                        <div className="p-3 border-b border-gray-100">
                          <p className="text-xs font-semibold text-gray-400 uppercase mb-2 flex items-center gap-1">
                            <Clock size={12} />
                            Recherches récentes
                          </p>
                          {recentSearches.map((search, i) => (
                            <button
                              key={i}
                              onClick={() => handleSearchSubmit(search)}
                              className="w-full flex items-center justify-between py-2 px-2 hover:bg-gray-50 rounded-lg group"
                            >
                              <span className="flex items-center gap-2 text-sm text-gray-700">
                                <Clock size={14} className="text-gray-400" />
                                {search}
                              </span>
                              <button
                                onClick={(e) => removeRecentSearch(search, e)}
                                className="p-1 hover:bg-gray-200 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X size={14} className="text-gray-400" />
                              </button>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Suggestions populaires */}
                      <div className="p-3">
                        <p className="text-xs font-semibold text-gray-400 uppercase mb-2 flex items-center gap-1">
                          <TrendingUp size={12} />
                          {searchQuery.trim() ? 'Suggestions' : 'Recherches populaires'}
                        </p>
                        {filteredSuggestions.map((suggestion, i) => (
                          <button
                            key={i}
                            onClick={() => handleSearchSubmit(suggestion.query)}
                            className="w-full flex items-center gap-3 py-2.5 px-2 hover:bg-orange-50 rounded-lg transition-colors"
                          >
                            <span className="text-lg">{suggestion.icon}</span>
                            <div className="flex-1 text-left">
                              <span className="text-sm font-medium text-gray-800">{suggestion.query}</span>
                              <span className="text-xs text-gray-400 ml-2">{suggestion.type}</span>
                            </div>
                            <ArrowRight size={14} className="text-gray-300" />
                          </button>
                        ))}
                      </div>
                      
                      {/* Rechercher avec le terme saisi */}
                      {searchQuery.trim() && (
                        <div className="p-3 border-t border-gray-100 bg-gray-50">
                          <button
                            onClick={() => handleSearchSubmit(searchQuery)}
                            className="w-full flex items-center justify-center gap-2 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-medium text-sm transition-colors"
                          >
                            <Search size={16} />
                            Rechercher "{searchQuery}"
                          </button>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ) : (
              <motion.div
                key="title"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex items-center gap-0.5"
              >
                <h1 className="text-lg font-bold text-[#1E3A5F]">Actualité</h1>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Boutons droite - Seulement Loupe */}
          {!showSearch && (
            <div className="flex items-center gap-1">
              <button 
                onClick={() => fetchPosts(true)}
                disabled={refreshing}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <RefreshCw size={20} className={`text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button 
                onClick={() => setShowSearch(true)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <Search size={20} className="text-gray-600" />
              </button>
            </div>
          )}
        </div>
        
        {/* Filtres principaux: Tout | À côté | Suivi | Offres */}
        <div className="flex border-b border-gray-100" style={{ backgroundColor: '#F8F9FB' }}>
          <button 
            onClick={() => setFilterTab('tout')}
            className={`flex-1 py-1.5 text-[13px] font-semibold flex items-center justify-center transition-colors ${
              filterTab === 'tout' 
                ? 'text-orange-600 border-b border-orange-500' 
                : 'text-gray-500'
            }`}
            data-testid="actu-tab-tout"
          >
            Tout
          </button>
          <button 
            onClick={() => setFilterTab('a_cote')}
            className={`flex-1 py-1.5 text-[13px] font-semibold flex items-center justify-center transition-colors ${
              filterTab === 'a_cote' 
                ? 'text-orange-600 border-b border-orange-500' 
                : 'text-gray-500'
            }`}
            data-testid="actu-tab-acote"
          >
            À côté
          </button>
          <button 
            onClick={() => setFilterTab('suivis')}
            className={`flex-1 py-1.5 text-[13px] font-semibold flex items-center justify-center transition-colors ${
              filterTab === 'suivis' 
                ? 'text-orange-600 border-b border-orange-500' 
                : 'text-gray-500'
            }`}
            data-testid="actu-tab-suivi"
          >
            Suivis
          </button>
          <button 
            onClick={() => setFilterTab('promos')}
            className={`flex-1 py-1.5 text-[13px] font-semibold flex items-center justify-center transition-colors ${
              filterTab === 'promos' 
                ? 'text-orange-600 border-b border-orange-500' 
                : 'text-gray-500'
            }`}
            data-testid="actu-tab-promos"
          >
            Promos
          </button>
        </div>

        {/* Sous-filtres Métiers - Scroll horizontal avec fond beige ultra léger */}
        <div className="relative">
          <div 
            className="flex border-b border-gray-100 overflow-x-auto"
            style={{ scrollbarWidth: 'none', backgroundColor: '#FFFBF7' }}
          >
            {METIERS.map(m => (
              <button
                key={m.value}
                onClick={() => setFilterMetier(m.value)}
                className={`flex-shrink-0 py-1 px-3 text-xs font-medium flex items-center justify-center transition-colors whitespace-nowrap ${
                  filterMetier === m.value 
                    ? 'text-orange-600 border-b border-orange-500' 
                    : 'text-gray-500'
                }`}
                data-testid={`actu-metier-${m.value}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          {/* Fade + flèche discrète à droite */}
          <div 
            className="absolute right-0 top-0 bottom-0 flex items-center pointer-events-none"
            style={{ 
              background: 'linear-gradient(to right, transparent, #FFFBF7 70%)',
              paddingLeft: '24px',
              paddingRight: '6px'
            }}
          >
            <ChevronRight size={14} className="text-gray-300" />
          </div>
        </div>
      </header>
      
      {/* Stories */}
      <div className="bg-white border-b border-gray-100">
        <StoriesBar onCreateStory={handleCreateStory} />
      </div>
      </div>
      
      {/* Contenu */}
      <div>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              {filterTab === 'suivis' ? (
                <User size={32} className="text-gray-400" />
              ) : filterTab === 'promos' ? (
                <Tag size={32} className="text-gray-400" />
              ) : filterTab === 'a_cote' ? (
                <MapPin size={32} className="text-gray-400" />
              ) : (
                <ImageIcon size={32} className="text-gray-400" />
              )}
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              {filterTab === 'suivis' 
                ? 'Aucune publication de tes abonnements' 
                : filterTab === 'promos' 
                  ? 'Aucune promo disponible'
                  : filterTab === 'a_cote'
                    ? 'Aucune publication à proximité'
                    : 'Aucune publication'}
            </h3>
            <p className="text-sm text-gray-500">
              {filterTab === 'suivis' 
                ? 'Suis des personnes ou boutiques pour voir leurs actus'
                : filterTab === 'promos'
                  ? 'Les promos et ventes flash apparaîtront ici'
                  : filterTab === 'a_cote'
                    ? 'Active ta localisation pour voir ce qui se passe autour de toi'
                    : 'Sois le premier à publier !'}
            </p>
          </div>
        ) : (
          <div>
            {filteredPosts.map((post, index) => (
              <PostCard key={post.id || index} post={post} />
            ))}
          </div>
        )}
      </div>
      
      {/* MobileBottomNav est géré globalement par MobileNavWrapper dans App.js */}
      
      {/* Popup Inscription pour Stories */}
      <AnimatePresence>
        {showAuthPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
            onClick={() => setShowAuthPopup(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Icône */}
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center">
                <ImageIcon size={32} className="text-orange-500" />
              </div>
              
              {/* Titre */}
              <h3 className="text-xl font-bold text-center text-gray-900 mb-2">
                Partage ta story !
              </h3>
              
              {/* Description */}
              <p className="text-sm text-gray-600 text-center mb-6">
                Inscris-toi gratuitement pour publier des stories, suivre des pros et interagir avec la communauté.
              </p>
              
              {/* Avantages */}
              <div className="space-y-2 mb-6">
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 text-xs">✓</span>
                  </div>
                  <span>Photos et vidéos jusqu'à 90 secondes</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 text-xs">✓</span>
                  </div>
                  <span>Ajoute du texte et des emojis</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 text-xs">✓</span>
                  </div>
                  <span>Visible pendant 7 jours</span>
                </div>
              </div>
              
              {/* Boutons */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowAuthPopup(false);
                    navigate('/inscription');
                  }}
                  className="w-full py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all"
                >
                  Créer mon compte gratuit
                </button>
                <button
                  onClick={() => {
                    setShowAuthPopup(false);
                    navigate('/connexion');
                  }}
                  className="w-full py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
                >
                  J'ai déjà un compte
                </button>
              </div>
              
              {/* Fermer */}
              <button
                onClick={() => setShowAuthPopup(false)}
                className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
